import type { MaterialPriceOpsUnit, MaterialUnit } from 'api/models/mainservice';
import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import type { TFunction } from 'i18next';
import type { MaterialSelectItem } from 'pages/material/models';
import type {
  BatchPriceUpdateType,
  PriceChangeCalculateModeType
} from 'pages/material/priceLists/models';

import { unitShortcut } from './materialHelpers';
import { mathRound, PRICE_DECIMAL_PLACES } from './mathHelpers';
import { useCustomTranslation } from './translationHelper';

export const currencyShortcut = (currencyId: string) => {
  return `currency.${currencyId}.abbr`;
};

export const currencyName = (currencyId: string) => {
  return `currency.${currencyId}.name`;
};

export const valueCurrencyShortcut = (value: number, currencyId: string, t: TFunction) => {
  return t(`currency.${currencyId}.value`, { value });
};

export const valueCurrencyUnitShortcut = (
  value: number,
  currencyId: string,
  unit: MaterialUnit,
  t: TFunction
) => {
  return `${valueCurrencyShortcut(value, currencyId, t)}/${t(unitShortcut(unit))}`;
};

export const usePriceTranslate = () => {
  const { tCommon } = useCustomTranslation();
  const localCurrencyId = useGetCompanyAndUserInfoQuery().data?.company?.currencyId || 'CZK';

  const translatePrice = (value: number, currencyId?: string) => {
    return tCommon(`currency_${currencyId || localCurrencyId}_value_formatted`, {
      value
    });
  };

  const translateCurrencyShortcut = (currencyId?: string) => {
    return tCommon(`currency_${currencyId || localCurrencyId}_abbr`);
  };

  const translateCurrencyName = (currencyId?: string) => {
    return tCommon(`currency_${currencyId || localCurrencyId}_name`);
  };

  return { translatePrice, translateCurrencyShortcut, translateCurrencyName };
};

export const getPercentFromPrices = (originalPrice: number, newPrice: number) => {
  if (originalPrice === 0 && newPrice !== 0) return 100;

  if (originalPrice === 0 && newPrice === 0) return 0;

  return Math.round(((newPrice - originalPrice) / originalPrice) * 100);
};

export const calculatePriceFromPercent = (originalPrice: number, percent: number) => {
  return mathRound(originalPrice + (originalPrice * percent) / 100, PRICE_DECIMAL_PLACES);
};

export const recalculatePrice = (
  originalPrice: number,
  amount: number,
  unit: MaterialPriceOpsUnit,
  mode: PriceChangeCalculateModeType
) => {
  if (unit === 'Percentage') {
    const percent = mode === 'discount' ? -amount : amount;
    return calculatePriceFromPercent(originalPrice, percent);
  }
  const value = mode === 'discount' ? -amount : amount;
  return originalPrice + value < 0 ? 0 : originalPrice + value;
};

export const recalculateBatchPrices = (
  material: MaterialSelectItem[],
  prices: BatchPriceUpdateType,
  amount: number | null,
  unit: MaterialPriceOpsUnit
) => {
  return Object.keys(prices).reduce((acc, key) => {
    const materialItem = material.find((m) => m.id === key);

    if (!materialItem) return acc;

    if (unit === 'Percentage') {
      return {
        ...acc,
        [key]: {
          value: calculatePriceFromPercent(materialItem.price || 0, amount ?? 0),
          percent: amount ?? 0
        }
      };
    }

    return {
      ...acc,
      [key]: {
        value: amount ? amount : materialItem.price,
        percent: amount ? getPercentFromPrices(materialItem.price || 0, amount) : 0
      }
    };
  }, {});
};
